/**
 * AuthenticatedShell — persistent-map layout for the authenticated routes.
 *
 * MapPage is mounted the FIRST time the user visits /map and then stays
 * mounted for the rest of the session. When the user navigates to /alerts,
 * /research, /admin, /api-keys, or /support, the map wrapper switches to
 * `display: none` — the React tree stays alive (all local state, refs,
 * MapLibre GL JS instance, DeckGL layers, chat, sketchpad, selection are
 * held in memory), but the DOM node is fully out of layout and paint.
 *
 * Why display:none and not visibility:hidden — visibility:hidden keeps the
 * element in the layout tree with its full 100vw × 100vh box, and the
 * many `zIndex: 9999` toolbars/panels inside MapPage stack against sibling
 * pages that use `position: fixed` (AdminPanel). Removing the wrapper from
 * layout with display:none avoids that interference entirely.
 *
 * On return to /map, MapLibre's ResizeObserver fires with the container's
 * new dimensions and the map redraws from its in-memory tile cache — no
 * network refetch, no state reset. If browser sizing produces a stretched
 * frame we can add a one-shot `map.resize()` on the transition.
 *
 * Trade-off: MapPage's polling hooks (useAlertMap, useMonitor, useSentiment)
 * keep running while the map is hidden. That's the point — the user asked
 * for the view to stick around, not for it to forget everything.
 */

import React, { useEffect, useRef, Suspense, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

const MapPage = React.lazy(() => import('./MapPage'));
const ResearchWorkspace = React.lazy(() => import('./ResearchWorkspace').then(m => ({ default: m.ResearchWorkspace })));
const AdminPanel = React.lazy(() => import('./AdminPanel').then(m => ({ default: m.AdminPanel })));
const AlertsPage = React.lazy(() => import('./AlertsPage'));
const ExpressionsPage = React.lazy(() => import('./ExpressionsPage'));
const AggregationsPage = React.lazy(() => import('./AggregationsPage'));
const BaseballCardsPage = React.lazy(() => import('./BaseballCardsPage'));
const SimulationPage = React.lazy(() => import('./SimulationPage'));
const ApiKeysPage = React.lazy(() => import('./ApiKeysPage'));
const SupportPage = React.lazy(() => import('./SupportPage').then(m => ({ default: m.SupportPage })));
const EyesUpReport = React.lazy(() => import('./EyesUpReport'));
const GeoSignalsReport = React.lazy(() => import('./GeoSignalsReport'));
const CustomUiPage = React.lazy(() => import('./CustomUiPage').then(m => ({ default: m.CustomUiPage })));
const CustomUiBuilderPage = React.lazy(() => import('./CustomUiBuilderPage').then(m => ({ default: m.CustomUiBuilderPage })));
const CustomUiRenderPage = React.lazy(() => import('./CustomUiRenderPage').then(m => ({ default: m.CustomUiRenderPage })));
import { UserProfile } from './UserProfile';

const LoadingFallback: React.FC = () => (
  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#94a3b8' }}>
    Loading...
  </div>
);

/** Phase 2 — one-time toast surfaced when the login handler couldn't
 *  resolve the user's chosen custom UI (deleted, forbidden, or never
 *  set). The flag is written to sessionStorage by LoginPage before the
 *  redirect so this read-once-and-clear path survives the navigation.
 *  Kept narrow on purpose: a shared toast queue would be nice but we
 *  don't have one yet and inlining avoids a cross-cutting dependency. */
const CustomUiLandingToast: React.FC = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem('customUiLandingToast') === 'missing-ref') {
        setVisible(true);
        sessionStorage.removeItem('customUiLandingToast');
        const t = setTimeout(() => setVisible(false), 6000);
        return () => clearTimeout(t);
      }
    } catch { /* private window / blocked storage — never show */ }
  }, []);
  if (!visible) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(0, 32, 63, 0.95)',
      border: '1px solid rgba(94,234,212,0.4)',
      color: '#e2e8f0',
      padding: '10px 16px', borderRadius: 8, fontSize: 13, zIndex: 10000,
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    }}>
      Your Custom UI landing page is missing — opened the default view instead.{' '}
      <a href="/custom-ui" style={{ color: '#5eead4', textDecoration: 'underline' }}>Manage UIs</a>
    </div>
  );
};

export const AuthenticatedShell: React.FC = () => {
  const location = useLocation();
  const onMap = location.pathname === '/map';

  // Latch: mount MapPage the first time the user opens /map, then keep it
  // mounted forever. Using a ref (not state) so the flip happens during
  // render — the first paint on /map goes straight into the Suspense
  // fallback for MapPage's lazy chunk, not into a blank frame.
  const hasMountedMapRef = useRef(false);
  if (onMap) hasMountedMapRef.current = true;

  return (
    <>
      <CustomUiLandingToast />
      {/* MapPage — mounted once, then persisted. display:none when off /map
          keeps the whole subtree out of layout so its high-z-index children
          can't stack over sibling routes like AdminPanel's fixed shell. */}
      {hasMountedMapRef.current && (
        <div
          aria-hidden={!onMap}
          style={{
            display: onMap ? 'block' : 'none',
            width: '100%',
            height: '100%',
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <MapPage />
          </Suspense>
        </div>
      )}

      {/* Non-map protected routes render when the map is hidden. The
          UserProfile pill is pinned bottom-right over the content so
          page toolbars can keep their full width — same corner the map
          page's merged profile+help button lives in, so the chrome
          position is consistent across routes. Ticket source defaults
          to 'research' here; routes with their own in-page ticket
          button (e.g. AlertsPage) still own that path. */}
      {!onMap && (
        <>
          <div
            style={{
              position: 'fixed', bottom: 20, right: 20, zIndex: 950,
            }}
          >
            <UserProfile source="research" />
          </div>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/research" element={<ResearchWorkspace />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/expressions" element={<ExpressionsPage />} />
              <Route path="/aggregations" element={<AggregationsPage />} />
              <Route path="/baseball-cards" element={<BaseballCardsPage />} />
              <Route path="/simulation" element={<SimulationPage />} />
              <Route path="/api-keys" element={<ApiKeysPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/eyes-up" element={<EyesUpReport />} />
              <Route path="/geosignals-report" element={<GeoSignalsReport />} />
              <Route path="/custom-ui" element={<CustomUiPage />} />
              <Route path="/custom-ui/builder/:uiId" element={<CustomUiBuilderPage />} />
              <Route path="/custom-ui/:uiId" element={<CustomUiRenderPage />} />
            </Routes>
          </Suspense>
        </>
      )}
    </>
  );
};
